import { useEffect, useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import meshifyVideo from "../assets/meshify-case.mp4";

export default function App() {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (prefersReducedMotion) {
      video.pause();
      return;
    }
    const play = video.play();
    if (play && typeof play.catch === "function") {
      play.catch(() => {
        /* autoplay blocked - ignore */
      });
    }
  }, [prefersReducedMotion]);

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} className="ch">
      <div className="ch-inner">
        <div className="ch-header">
          <motion.span className="ch-eyebrow" {...fade(0)}>
            <span className="ch-eyebrow-mark" />
            AI Workstation Configurations
          </motion.span>

          <motion.h2 className="ch-title" {...fade(0.1)}>
            Professional AI Workstations for{" "}
            <span className="ch-title-accent">LLM Training and Inference</span>
          </motion.h2>
        </div>

        <div className="ch-body-wrap">
          <motion.p className="ch-body ch-body-lead" {...fade(0.2)}>
            ArsenalPC builds liquid-cooled systems for LLM training, machine
            learning research, and GPU-accelerated inference.
          </motion.p>

          <motion.p className="ch-body" {...fade(0.3)}>
            From single RTX 5090 inference builds to dual RTX Pro 6000 systems
            for 70B model training, each configuration delivers sustained
            performance under continuous compute loads.
          </motion.p>
        </div>

        <motion.div
          className="ch-visual"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={
            shouldAnimate ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1 }
          }
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="ch-video-frame">
            <video
              ref={videoRef}
              className="ch-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              aria-hidden="true"
              tabIndex={-1}
            >
              <source src={meshifyVideo} type="video/mp4" />
            </video>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
